import { Check, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

/**
 * Einzelne Passwort-Regel.
 * `label` ist optional: die Standard-Regeln tragen nur ihren `key` und werden über i18n
 * (`auth:passwordRules.<key>`) angezeigt. Aufrufer mit eigenen Regeln können weiterhin ein
 * explizites `label` übergeben, das dann Vorrang hat.
 */
export interface PasswordRule {
  key: string;
  label?: string;
  test: (pw: string) => boolean;
}

/**
 * Standard-Regeln, abgeleitet aus der Cognito User Pool Config (CDK auth-stack.ts).
 * Aenderungen an der Cognito-Policy muessen hier gespiegelt werden.
 * Kann per Props ueberschrieben werden, wenn andere Regeln gelten.
 * Anzeige-Texte liegen im i18n-Namespace `auth` unter `passwordRules.<key>`.
 */
export const DEFAULT_PASSWORD_RULES: PasswordRule[] = [
  { key: 'minLength', test: (pw: string) => pw.length >= 12 },
  { key: 'uppercase', test: (pw: string) => /[A-Z]/.test(pw) },
  { key: 'lowercase', test: (pw: string) => /[a-z]/.test(pw) },
  { key: 'digit', test: (pw: string) => /\d/.test(pw) },
  { key: 'special', test: (pw: string) => /[^A-Za-z0-9]/.test(pw) },
];

interface PasswordRequirementsProps {
  password: string;
  /** Optionale Regeln — Standard: DEFAULT_PASSWORD_RULES (aus Cognito Pool Config) */
  rules?: PasswordRule[];
  className?: string;
}

/**
 * Echtzeit-Passwort-Validierungs-Checkliste.
 * Zeigt fuer jede Regel ein Haekchen (erfuellt) oder Kreuz (nicht erfuellt).
 *
 * @see Spec 0043 Section 4.2/4.3 — Passwort-Anforderungen
 */
export function PasswordRequirements({ password, rules = DEFAULT_PASSWORD_RULES, className }: PasswordRequirementsProps) {
  const { t } = useTranslation('auth');
  return (
    <ul className={cn('space-y-1 text-sm', className)}>
      {rules.map((rule) => {
        const passed = password.length > 0 && rule.test(password);
        return (
          <li
            key={rule.key}
            className={cn(
              'flex items-center gap-2',
              passed ? 'text-green-600 dark:text-green-400' : 'text-muted-foreground'
            )}
          >
            {passed ? (
              <Check className="h-3.5 w-3.5 flex-shrink-0" />
            ) : (
              <X className="h-3.5 w-3.5 flex-shrink-0" />
            )}
            <span>{rule.label ?? t(`passwordRules.${rule.key}`)}</span>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Prueft ob ein Passwort alle Anforderungen erfuellt.
 * Kann sowohl in Komponenten als auch in Validierungs-Logik verwendet werden.
 */
export function isPasswordValid(password: string, rules: PasswordRule[] = DEFAULT_PASSWORD_RULES): boolean {
  return rules.every((rule) => rule.test(password));
}
